import { Box } from "@mui/material";
import { ChangeEvent, FormEvent, useState } from "react";
import { SearchForm } from "../../components/common/SearchForm";
import {
  RadioOption,
  TableTitle,
} from "../../components/mobileSize/TableTitle";
import { DotLoader } from "../../components/dotLoader";
import { GetMemberResult, Member, SystemSetting } from "../../types/types";
import { Button } from "../../components/common/Button";
import theme from "../../theme";
import Spinner from "../../components/spinner";
import styled from "@emotion/styled";
import { MobileTable } from "../../components/mobileSize/MobileTable";
import {
  DetailRecordIcon,
  DetailUserIcon,
  PwResetIcon,
  TrashCanIcon,
} from "../../assets/icons/icons";
import { CustomTooltip } from "../../components/common/CustomTooltip";
import { formatDate } from "../../utils/util";
import { PhoneMaskToggle } from "../../components/PhoneMaskToggle";
import userStorage from "../../utils/userStorage";
import { useI18n } from "../../locales";

const SORT_LIST: RadioOption[] = [
  { label: `기본 순`, value: `` },
  { label: `계정명 순`, value: `name` },
  { label: `ID 순`, value: `id` },
  { label: `연락처 순`, value: `phone` },
  { label: `E-mail 순`, value: `email` },
  { label: `주소 순`, value: `address` },
  { label: `상세주소 순`, value: `address_detail` },
  { label: `계정생성일시 순`, value: `create_date` },
];

type Prop = {
  data?: GetMemberResult;
  isLoading: boolean;
  systemSetting: SystemSetting | null;
  onClickBtn: (input: string) => void;
  sort?: string;
  setSort?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setOpenRegisterModal: React.Dispatch<React.SetStateAction<boolean>>;
  onClickDetailIcon: (el: Member) => void;
  onClickDeleteIcon: (el: Member) => void;
  onClickRecordIcon: (el: Member) => void;
  onClickResetIcon: () => void;
};

export const MobileManageUser = ({
  data,
  isLoading,
  systemSetting,
  onClickBtn,
  sort,
  setSort,
  setOpenRegisterModal,
  onClickDetailIcon,
  onClickDeleteIcon,
  onClickRecordIcon,
  onClickResetIcon,
}: Prop) => {
  const { t } = useI18n();
  const [openFilter, setOpenFilter] = useState(false);
  const [input, setInput] = useState("");
  const user = userStorage.get();

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInput(event.currentTarget.value);
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onClickBtn(input);
  };

  return (
    <>
      {/* 필터 섹션 */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: openFilter ? "auto" : "0px",
          maxHeight: openFilter ? "100vh" : "0px",
          overflow: openFilter ? "visible" : "hidden",
          gap: "6px",
          paddingBottom: openFilter ? "16px" : "0px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: `4px`,
            alignItems: "center",
          }}
        >
          <SearchForm
            onSubmit={onSubmit}
            placeholder={t.manageUser.searchPlaceholder}
            onChange={onChange}
            inputValue={input}
            setInputValue={setInput}
            width="140px"
            gap="6px"
            bottom="0"
            marginR="0"
          />
          <Button
            bgColor={theme.color.aquamarine_500}
            hoverColor={theme.color.aquamarine_600}
            height="30px"
            isDisabled={!systemSetting?.create}
            width="96px"
            onClick={() => setOpenRegisterModal(true)}
            paddingX="0px"
          >
            {t.manageUser.addUser}
          </Button>
        </Box>
      </Box>

      {/* 테이블 타이틀 섹션 */}
      <TableTitle
        title={
          isLoading ? (
            <div style={{ height: "15px" }}>
              <DotLoader height="6px" />
            </div>
          ) : (
            `${t.manageUser.userAccountInfo} - ${data?.total_items ?? 0} 건`
          )
        }
        sort={sort}
        setSort={setSort}
        setOpenFilter={setOpenFilter}
        sortList={SORT_LIST}
      />

      {/* 테이블 섹션 */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          flexGrow: 1,
        }}
      >
        {isLoading && <Spinner />}
        {data &&
          data.list.length > 0 &&
          data.list.map((el) => (
            <MobileTable
              key={el.idx.toString()}
              defaultInfoGap="0px"
              defaultInfo={
                <>
                  <TextWrapper>
                    {/* 계정명 */}
                    <CustomTooltip title={el?.name} active>
                      <Text $width="31%">{el.name}</Text>
                    </CustomTooltip>
                    {/* 아이디 */}
                    <CustomTooltip title={el?.id} active>
                      <Text $width="31%">{el.id}</Text>
                    </CustomTooltip>
                    {/* 연락처 */}
                    {/* <CustomTooltip title={el?.phone} active={!!el.phone}> */}
                    <Text $width="33%" style={{ textAlign: "right" }}>
                      {/* {el.phone ? el.phone : "연락처 없음"} */}
                      <PhoneMaskToggle
                        text={el.phone}
                        who={3} // 사용자
                        userIdx={user?.idx}
                        name={el.name}
                        isMobile
                      />
                    </Text>
                    {/* </CustomTooltip> */}
                  </TextWrapper>
                  <TextWrapper>
                    {/* 주소 */}
                    <CustomTooltip
                      title={el?.address}
                      active={!!el.address}
                    >
                      <Text>{el.address ? el.address : "주소 없음"}</Text>
                    </CustomTooltip>
                  </TextWrapper>
                  <TextWrapper>
                    {/* 이메일 */}
                    <CustomTooltip title={el?.email} active={!!el.email}>
                      <Text>{el.email ? el.email : "이메일 없음"}</Text>
                    </CustomTooltip>
                  </TextWrapper>
                </>
              }
              detailInfo={
                <>
                  <Table>
                    <tbody>
                      {/* 상세주소*/}
                      <DetailWrapper>
                        <DetailTitle>{`상세주소`}</DetailTitle>
                        <CustomTooltip
                          title={el?.address_detail}
                          active={!!el.address_detail}
                        >
                          <DetailText>
                            {el.address_detail
                              ? el.address_detail
                              : "상세 주소 없음"}
                          </DetailText>
                        </CustomTooltip>
                      </DetailWrapper>
                      {/* 계정생성일시 */}
                      <DetailWrapper>
                        <DetailTitle>{`계정생성일시`}</DetailTitle>
                        <DetailText>
                          {formatDate(el.create_date, "yyyy-MM-dd HH:mm")}
                        </DetailText>
                      </DetailWrapper>
                    </tbody>
                  </Table>
                  <IconsWrapper>
                    {/* 정보 상세 보기 */}
                    <IconWrapper onClick={() => onClickDetailIcon(el)}>
                      <DetailUserIcon />
                    </IconWrapper>
                    {/* 연락처 열람 기록 보기 */}
                    <IconWrapper
                      onClick={() => {
                        onClickRecordIcon(el);
                      }}
                    >
                      <DetailRecordIcon />
                    </IconWrapper>
                    {/* 계정 삭제하기 */}
                    <IconWrapper
                      onClick={() => {
                        if (!systemSetting?.delete) return;
                        onClickDeleteIcon(el);
                      }}
                    >
                      <TrashCanIcon />
                    </IconWrapper>

                    {/*  비밀번호 초기화하기 */}
                    <IconWrapper
                      onClick={onClickResetIcon}
                      $iconWH={"24px"}
                    >
                      <PwResetIcon />
                    </IconWrapper>
                  </IconsWrapper>
                </>
              }
            />
          ))}
        {!isLoading && data && data.list.length === 0 && (
          <NoDataMassage>조회된 데이터가 없습니다.</NoDataMassage>
        )}
      </Box>
    </>
  );
};

const NoDataMassage = styled.div`
  text-align: center;
  color: ${theme.color.neutral_500};
  padding-top: 200px;
`;

const TextWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const Text = styled.div<{ $fontSize?: string; $width?: string }>`
  font-size: ${({ $fontSize }) =>
    $fontSize ? $fontSize : theme.fontSize.FONT_2};
  flex: ${({ $width }) => ($width ? `0 1 ${$width}` : "1")};
  width: ${({ $width }) => ($width ? $width : "calc(100% - 32px)")};
  max-width: ${({ $width }) =>
    $width ? $width : "calc(100vw- 32px- 32px)"};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Table = styled.table`
  border-collapse: collapse;
  border-spacing: 0;
  width: 100%;

  td {
    border: 0.5px solid ${theme.color.neutral_100};
  }
`;

const DetailWrapper = styled.tr`
  display: flex;
  height: 24px;
  border-collapse: collapse;
`;
const DetailTitle = styled.td<{ $fontWeight?: string }>`
  width: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fontSize.FONT_2};
  font-weight: ${({ $fontWeight }) =>
    $fontWeight ? $fontWeight : theme.fontWeight.NORMAL};
  padding: 4px 8px;
  background-color: ${theme.color.neutral_50};
`;
const DetailText = styled.td<{ $fontWeight?: string }>`
  flex-grow: 1;
  font-size: ${theme.fontSize.FONT_2};
  font-weight: ${({ $fontWeight }) =>
    $fontWeight ? $fontWeight : theme.fontWeight.NORMAL};
  padding: 4px 8px;
  max-width: calc(100vw - 154px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IconsWrapper = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  gap: 16px;
  margin-top: 6px;
`;

const IconWrapper = styled.button<{
  $wrapperWH?: string;
  $iconWH?: string;
}>`
  width: ${({ $wrapperWH }) => ($wrapperWH ? $wrapperWH : `28px`)};
  height: ${({ $wrapperWH }) => ($wrapperWH ? $wrapperWH : `28px`)};
  border-radius: 50%;
  background: ${theme.color.neutral_200};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: ${({ $iconWH }) => ($iconWH ? $iconWH : `14px`)};
    height: ${({ $iconWH }) => ($iconWH ? $iconWH : `14px`)};
  }
`;
